/**
 * Channel bookmarks (Slack's bookmarks bar, docs/design/slack-gap.md): links pinned under a conversation's header,
 * shared by its members. Any member adds, edits or removes them (not in archived channels). Changes reach the other
 * members as `channel.updated` (an open conversation refetches its bookmarks), so no new event type is needed.
 *
 * Like Slack, a bookmark is a link, a folder (one level deep; bookmarks inside carry its `parentId`), a file shared
 * in the workspace, or a Bundle page. Order is `rank` (a fractional-indexing key, like the sidebar's), changed by dragging.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

/** Most bookmarks a conversation keeps (Slack: 100). */
export const MAX_BOOKMARKS = 100;

/** Absent on a bookmark = "link". */
export const BookmarkKind = z.enum(["link", "folder", "file", "page"]);
export type BookmarkKind = z.infer<typeof BookmarkKind>;

export const ChannelBookmark = z.object({
  id: Id,
  channelId: Id,
  title: z.string().min(1).max(200),
  /** Links: an http(s) URL. Folders, files and pages: "". */
  url: z.string().max(2048),
  createdBy: Id,
  createdAt: Timestamp,
  /** Absent = "link". */
  kind: BookmarkKind.optional(),
  /** The folder this bookmark sits in (absent = the bar itself). Folders never have one. */
  parentId: Id.optional(),
  /** Sort key within the bar or the folder (fractional-indexing, ascending; ties by `createdAt`). The server always sends it. */
  rank: z.string().max(64).optional(),
  /** kind "file": the file (opens the file page). */
  fileId: Id.optional(),
  /** kind "file": its type, for the icon. */
  mime: z.string().max(255).optional(),
  /** kind "page": the page (opens it). */
  pageId: Id.optional(),
});
export type ChannelBookmark = z.infer<typeof ChannelBookmark>;

/** GET /v1/w/:ws/channels/:channelId/bookmarks — in bar order (oldest first). */
export const ListBookmarksResponse = z.object({ bookmarks: z.array(ChannelBookmark) });
export type ListBookmarksResponse = z.infer<typeof ListBookmarksResponse>;

/**
 * POST …/bookmarks (add) and PATCH …/bookmarks/:bookmarkId (edit: title and, for links, url; kind / parent / file /
 * page are fixed at creation — use move for the folder). `url` is "" for folders, files and pages.
 */
export const BookmarkRequest = z.object({
  title: z.string().min(1).max(200),
  url: z.string().max(2048),
  kind: BookmarkKind.optional(),
  parentId: Id.optional(),
  fileId: Id.optional(),
  mime: z.string().max(255).optional(),
  pageId: Id.optional(),
});
export type BookmarkRequest = z.infer<typeof BookmarkRequest>;

export const BookmarkResponse = z.object({ bookmark: ChannelBookmark });
export type BookmarkResponse = z.infer<typeof BookmarkResponse>;

/**
 * POST …/bookmarks/:bookmarkId/move — drag to reorder, or into / out of a folder: the new folder (absent = the bar)
 * and sort key (core logic/bookmarks `moveRank`). Folders stay in the bar. → BookmarkResponse (channel.updated).
 */
export const MoveBookmarkRequest = z.object({ parentId: Id.optional(), rank: z.string().min(1).max(64) });
export type MoveBookmarkRequest = z.infer<typeof MoveBookmarkRequest>;
